import { IconDownload, IconHeart, IconHeartFilled, IconShare2 } from '@tabler/icons-react'
import { Loading } from 'react-daisyui'

interface ActionButton {
  onClick: () => void
  busy?: boolean
}

export interface HeartDescriptor {
  /** Filled heart (liked / selected). */
  active: boolean
  busy?: boolean
  onClick: () => void
  title?: string
}

interface FileActionBarProps {
  share?: ActionButton
  download?: ActionButton
  heart?: HeartDescriptor
  /** Desktop hover state. */
  hovered: boolean
  /** Render the larger heart on mobile (used for already-selected/liked files). */
  showMobileHeart?: boolean
  /** Keep the desktop heart visible without hover (e.g. while selection is active). */
  alwaysShowHeart?: boolean
}

const BTN =
  'flex items-center justify-center w-8 h-8 rounded-full bg-[black] bg-opacity-45 hover:bg-opacity-70 text-[white] shadow transition-colors'
// The heart is clearly larger than the share/download buttons on desktop too.
const HEART_BTN =
  'flex items-center justify-center w-11 h-11 rounded-full bg-[black] bg-opacity-45 hover:bg-opacity-70 text-[white] shadow transition-colors'

/**
 * Per-file action bar over a gallery tile.
 * - Desktop: share / download appear on hover; the heart appears on hover OR
 *   whenever it is active (liked / selected). The container never captures
 *   pointer events, so empty space keeps opening the lightbox.
 * - Mobile: a larger heart, shown when `showMobileHeart` (already liked/selected,
 *   or while a selection is active on prof drives).
 */
export default function FileActionBar({ share, download, heart, hovered, showMobileHeart, alwaysShowHeart }: FileActionBarProps) {
  if (!share && !download && !heart) {
    return null
  }
  const stop = (e: React.MouseEvent) => e.stopPropagation()
  const heartVisible = hovered || !!heart?.active || !!alwaysShowHeart
  const hoverClasses = (on: boolean) =>
    `transition-opacity duration-200 ${on ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`

  return (
    <>
      {/* Forgiving hit area in the bottom-right corner: a click/tap near the
          heart — even just outside the visible circle, in the tile corner —
          still toggles it instead of opening the lightbox. Rendered before the
          bars so the real share/download/heart buttons stack on top and keep
          their own clicks; this only catches what would otherwise fall through
          to the tile. */}
      {heart && (
        <>
          <button
            type='button'
            aria-hidden='true'
            tabIndex={-1}
            title={heart.title || 'Auswählen'}
            className={`hidden md:block absolute bottom-0 right-0 w-14 h-14 ${heartVisible ? 'pointer-events-auto' : 'pointer-events-none'}`}
            onClick={(e) => {
              stop(e)
              heart.onClick()
            }}
          />
          {showMobileHeart && (
            <button
              type='button'
              aria-hidden='true'
              tabIndex={-1}
              title={heart.title || 'Auswählen'}
              className='md:hidden absolute bottom-0 right-0 w-16 h-16 pointer-events-auto'
              onClick={(e) => {
                stop(e)
                heart.onClick()
              }}
            />
          )}
        </>
      )}

      {/* Desktop bar */}
      <div className='absolute inset-x-0 bottom-0 hidden md:flex justify-end items-center gap-1.5 p-2 pointer-events-none'>
        {share && (
          <button
            type='button'
            title='Teilen'
            className={`${BTN} ${hoverClasses(hovered)}`}
            onClick={(e) => {
              stop(e)
              share.onClick()
            }}
          >
            {share.busy ? <Loading size='xs' /> : <IconShare2 size={18} />}
          </button>
        )}
        {download && (
          <button
            type='button'
            title='Herunterladen'
            className={`${BTN} ${hoverClasses(hovered)}`}
            onClick={(e) => {
              stop(e)
              download.onClick()
            }}
          >
            {download.busy ? <Loading size='xs' /> : <IconDownload size={18} />}
          </button>
        )}
        {heart && (
          <button
            type='button'
            title={heart.title || 'Auswählen'}
            className={`${HEART_BTN} ${hoverClasses(heartVisible)}`}
            onClick={(e) => {
              stop(e)
              heart.onClick()
            }}
          >
            {heart.busy ? <Loading size='sm' /> : heart.active ? <IconHeartFilled size={26} /> : <IconHeart size={26} />}
          </button>
        )}
      </div>

      {/* Mobile: larger heart, always visible when enabled */}
      {heart && showMobileHeart && (
        <div className='md:hidden absolute bottom-2 right-2'>
          <button
            type='button'
            title={heart.title || 'Auswählen'}
            className='flex items-center justify-center w-12 h-12 rounded-full bg-[black] bg-opacity-45 text-[white] shadow pointer-events-auto'
            onClick={(e) => {
              stop(e)
              heart.onClick()
            }}
          >
            {heart.busy ? <Loading size='sm' /> : heart.active ? <IconHeartFilled size={30} /> : <IconHeart size={30} />}
          </button>
        </div>
      )}
    </>
  )
}
